import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import {
	createClassification,
	createKind,
	deleteClassification,
	deleteKind,
	getClassificationList,
	getKindList,
	updateClassification,
	updateKind,
} from '../services/catalog'

// --- Kind ---

import type {
	ApiResponse,
	ClassificationWithSubsListOutput,
	KindWithRisksListOutput,
} from '@/types'

export function useKindList() {
	return useQuery<
		ApiResponse<KindWithRisksListOutput>,
		Error,
		KindWithRisksListOutput
	>({
		queryKey: ['catalog', 'kind'],
		queryFn: getKindList,
		staleTime: 10 * 60 * 1000, // 10 minutos
	})
}

export function useCreateKind() {
	const queryClient = useQueryClient()
	return useMutation({
		mutationFn: createKind,
		onSuccess: () => {
			queryClient.invalidateQueries({ queryKey: ['catalog', 'kind'] })
		},
	})
}

export function useUpdateKind() {
	const queryClient = useQueryClient()
	return useMutation({
		mutationFn: ({
			id,
			data,
		}: {
			id: string
			data: {
				name: string
				spheraName?: string | null
				risks: { id?: string; name: string }[]
			}
		}) => updateKind(id, data),
		onSuccess: () => {
			queryClient.invalidateQueries({ queryKey: ['catalog', 'kind'] })
		},
	})
}

export function useDeleteKind() {
	const queryClient = useQueryClient()
	return useMutation({
		mutationFn: deleteKind,
		onSuccess: () => {
			queryClient.invalidateQueries({ queryKey: ['catalog', 'kind'] })
		},
	})
}

// --- Classification ---

export function useClassificationList() {
	return useQuery<
		ApiResponse<ClassificationWithSubsListOutput>,
		Error,
		ClassificationWithSubsListOutput
	>({
		queryKey: ['catalog', 'classification'],
		queryFn: getClassificationList,
		staleTime: 10 * 60 * 1000, // 10 minutos
	})
}

export function useCreateClassification() {
	const queryClient = useQueryClient()
	return useMutation({
		mutationFn: createClassification,
		onSuccess: () => {
			queryClient.invalidateQueries({ queryKey: ['catalog', 'classification'] })
		},
	})
}

export function useUpdateClassification() {
	const queryClient = useQueryClient()
	return useMutation({
		mutationFn: ({
			id,
			data,
		}: {
			id: string
			data: {
				name: string
				spheraName?: string | null
				subclassifications: { id?: string; name: string }[]
			}
		}) => updateClassification(id, data),
		onSuccess: () => {
			queryClient.invalidateQueries({ queryKey: ['catalog', 'classification'] })
		},
	})
}

export function useDeleteClassification() {
	const queryClient = useQueryClient()
	return useMutation({
		mutationFn: deleteClassification,
		onSuccess: () => {
			queryClient.invalidateQueries({ queryKey: ['catalog', 'classification'] })
		},
	})
}
